import PubSub from 'pubsub-js';
import { Vector3 } from 'three';

import { DynamicAssetType } from '../../../typings/api/skymap/rest/v1/.common';
import { SectionExpandState } from '../../react/components/skyview/right-menu/tools/design-tool-menu/design-tool-menu';
import { Camera, Marker } from '../components/gcp/gcp-lib';
import { SkyviewCamera } from '../viewer/cameras/types';
import { Comment } from '../viewer/elements/comment';
import { DxfUnit } from '../viewer/elements/dxf/types';
import { AssetCategory, ElementObj } from '../viewer/elements/element-obj';
import { VolumeModelState } from '../viewer/elements/volume/types';
import { VolumeHandler } from '../viewer/elements/volume/volume-handler';
import { SceneType } from '../viewer/scene-manager';
import { Coordinates } from '../viewer/utilities/math-helpers';
import { WmsToolLayerItem } from '../viewer/wms-state';

export enum SubscriptionTopic {
  ShowSetAssetCoordinateDialog,
  OnReferencePointTransformCoordinateASet,
  OnReferencePointTransformCoordinateBSet,
  SkyviewAssetCategoryVisibilityChanged,
  SkyviewAssetFocusChanged,
  SkyviewAssetVisibilityChanged,
  WmsAttributionsChanged,
  ToastrMessage,
  DesignToolMultiselectUpdated,
  DesignToolActiveFeatureChanged,
  DesignToolMenuStateChanged,
  DesignToolActiveActionChanged,
  DesignAttributesChanged,
  DesignDoubleSideChanged,
  RefreshAssetList,
  ModelVolumeStateChanged,
  SceneScaleChanged,
  ShowAssetMenuCheckBoxes,
  HideAssetMenuCheckBoxes,
  ShowOnlySpecificAssets,
  ShowAssetMenuOverlay,
  HideAssetMenuOverlay,
  SaveAssetMenuVisibilitySnapshot,
  RestoreAssetMenuVisibilitySnapshot,
  AssetMenuCategoryCheckBoxesChanged,
  OnSceneChangeStarted,
  OnSceneChangeCompleted,
  OnSkyViewWmsMapToggled,
  OnSkyViewWmsLayerChanged,
  OnCameraMoved,
  OnCameraChanged,
  ErrorFetchingProjectInfo,
  ShowEditCommentDialog,
  OnProjectionUpdated = 'onProjectionUpdated',
  ShowCamerasDialog = 'showCamerasDialog',
  ShowMarkersDialog = 'showMarkersDialog',
  GcpXmlChanged = 'gcpXmlChanged',
  SkyviewLoadingProgress = 'skyviewLoadingProgress',
  AssetListChanged = 'assetListChanged',
  AssetChanged = 'assetChanged',
  SetTool = 'setTool',
  UnSelection = 'unSelection',
  NewSelection = 'newSelection',
  RightMenuToggled = 'rightMenuToggled',
  VolumeInspectorToggleModal = 'volumeInspectorToggleModal',
  I18NextLanguageChanged = 'I18NextLanguageChanged',
}

type OnReferencePointTransformCoordinateASet = Coordinates;

type WmsAttributionsChanged = string[];

type OnProjectionUpdated = {
  old: { projection: string; origo?: Vector3 };
  new: { projection: string; origo?: Vector3 };
};

type OnReferencePointTransformCoordinateBSet = Coordinates;

type ShowEditCommentDialog = {
  comment: Comment;
};

type ShowCamerasDialog = {
  cameras: Camera[];
};

type ShowMarkersDialog = {
  markers: Marker[];
};

type ShowSetAssetCoordinateDialog = {
  originWorldPosition?: Vector3;
  asset: ElementObj;
  infoMessage?: string;
  title?: string;
};

type SkyviewAssetCategoryVisibilityChanged = {
  categoryType: AssetCategory;
  enabled: boolean;
};

type SkyviewAssetVisibilityChanged = {
  assetUuid: string;
  visible: boolean;
};

type SkyviewAssetFocusChanged = {
  /**
   * Id of asset focused or undefined if no asset is currently focused.
   */
  assetUuid: string | undefined;
};

type ToastrMessage = {
  type: 'warn' | 'error' | 'success';
  title?: string;
  message: string;
};

type SkyviewLoadingProgress = {
  progress: number;
};

type AssetListChanged = {
  assets?: THREE.Object3D[];
};

type VolumeInspectorToggleModal = {
  display: boolean;
  volumeHandler: VolumeHandler;
};

type RefreshAssetList = {
  projectId: string;
  assetTypes: DynamicAssetType[];
};

type VolumeModelStateChanged = {
  volumeId: string;
  state: VolumeModelState;
};

type ShowAssetMenuCheckBoxes = {
  categories: AssetCategory[];
  autoCheckVisibleAssets?: boolean;
  autoCheckAssetIds?: string[];
  disableAll?: boolean;
};

type ShowOnlySpecificAssets = {
  assetUuids: string[];
};

type AssetMenuCategoryCheckBoxesChanged = {
  category: AssetCategory;
  checkedAssetUuids: string[];
};

type OnSceneChange = {
  scene: SceneType;
};

type OnCameraMoved = {
  camera: SkyviewCamera;
};

type OnWmsLayerChanged = {
  layerItem: WmsToolLayerItem;
};

// Map subscription topic to message type.
// prettier-ignore
export type PubSubMessageType<T> = T extends SubscriptionTopic.SkyviewAssetCategoryVisibilityChanged
  ? SkyviewAssetCategoryVisibilityChanged
  : T extends SubscriptionTopic.SkyviewAssetVisibilityChanged
  ? SkyviewAssetVisibilityChanged
  : T extends SubscriptionTopic.OnReferencePointTransformCoordinateASet
  ? OnReferencePointTransformCoordinateASet
  : T extends SubscriptionTopic.OnReferencePointTransformCoordinateBSet
  ? OnReferencePointTransformCoordinateBSet
  : T extends SubscriptionTopic.SkyviewAssetFocusChanged
  ? SkyviewAssetFocusChanged
  : T extends SubscriptionTopic.ToastrMessage
  ? ToastrMessage
  : T extends SubscriptionTopic.WmsAttributionsChanged
  ? WmsAttributionsChanged
  : T extends SubscriptionTopic.SkyviewLoadingProgress
  ? SkyviewLoadingProgress
  : T extends SubscriptionTopic.AssetChanged
  ? ElementObj
  : T extends SubscriptionTopic.OnProjectionUpdated
  ? OnProjectionUpdated
  : T extends SubscriptionTopic.ShowSetAssetCoordinateDialog
  ? ShowSetAssetCoordinateDialog
  : T extends SubscriptionTopic.AssetListChanged
  ? AssetListChanged
  : T extends SubscriptionTopic.SetTool
  ? string
  : T extends SubscriptionTopic.NewSelection
  ? ElementObj
  : T extends SubscriptionTopic.RightMenuToggled
  ? boolean
  : T extends SubscriptionTopic.DesignToolMenuStateChanged
  ? Partial<SectionExpandState>
  : T extends SubscriptionTopic.VolumeInspectorToggleModal
  ? VolumeInspectorToggleModal
  : T extends SubscriptionTopic.ShowEditCommentDialog
  ? ShowEditCommentDialog
  : T extends SubscriptionTopic.RefreshAssetList
  ? RefreshAssetList
  : T extends SubscriptionTopic.ModelVolumeStateChanged
  ? VolumeModelStateChanged
  : T extends SubscriptionTopic.SceneScaleChanged
  ? DxfUnit
  : T extends SubscriptionTopic.ShowAssetMenuCheckBoxes
  ? ShowAssetMenuCheckBoxes
  : T extends SubscriptionTopic.HideAssetMenuCheckBoxes
  ? undefined
  : T extends SubscriptionTopic.ShowOnlySpecificAssets
  ? ShowOnlySpecificAssets
  : T extends SubscriptionTopic.ShowAssetMenuOverlay
  ? undefined
  : T extends SubscriptionTopic.HideAssetMenuOverlay
  ? undefined
  : T extends SubscriptionTopic.SaveAssetMenuVisibilitySnapshot
  ? undefined
  : T extends SubscriptionTopic.AssetMenuCategoryCheckBoxesChanged
  ? AssetMenuCategoryCheckBoxesChanged
  : T extends SubscriptionTopic.ShowCamerasDialog
  ? ShowCamerasDialog
  : T extends SubscriptionTopic.ShowMarkersDialog
  ? ShowMarkersDialog
  : T extends SubscriptionTopic.OnSceneChangeStarted
  ? OnSceneChange
  : T extends SubscriptionTopic.OnSceneChangeCompleted
  ? OnSceneChange
  : T extends SubscriptionTopic.OnCameraChanged
  ? OnCameraMoved
  : T extends SubscriptionTopic.OnCameraMoved
  ? OnCameraMoved
  : T extends SubscriptionTopic.OnSkyViewWmsLayerChanged
  ? OnWmsLayerChanged
  : undefined;

export function publish<T extends SubscriptionTopic>(topic: T, message?: PubSubMessageType<T>) {
  PubSub.publish(topic, message);
}

export function subscribe<T extends SubscriptionTopic>(
  topic: T,
  func: (message: PubSubMessageType<T>) => void,
) {
  return PubSub.subscribe(topic, (topic: T, message: PubSubMessageType<T>) => {
    // Don't pass topic as we only subscribe on one topic at a time.
    func(message);
  });
}

export function unsubscribe<T extends SubscriptionTopic>(topic: T) {
  PubSub.unsubscribe(topic);
}
